import React from "react";
import PropTypes from "prop-types";
import {
  Popover,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
} from "@mui/material";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";

// HOC that provides only the popover UI structure
const withPopover = (WrappedComponent) => {
  const WithPopoverComponent = ({ open, anchorEl, onClose, ...props }) => {
    return (
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={onClose}
        disableRestoreFocus
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            marginLeft: 3,
          },
        }}
      >
        <WrappedComponent {...props} onClose={onClose} />
      </Popover>
    );
  };

  // PropTypes for the HOC
  WithPopoverComponent.propTypes = {
    open: PropTypes.bool.isRequired,
    anchorEl: PropTypes.object,
    onClose: PropTypes.func.isRequired,
  };

  // Default props
  WithPopoverComponent.defaultProps = {
    anchorEl: null,
  };

  // Set display name for debugging
  WithPopoverComponent.displayName = `withPopover(${WrappedComponent.displayName || WrappedComponent.name || "Component"})`;

  return WithPopoverComponent;
};

// SubMenu content component
const SubMenuContent = ({
  userInfo, // { firstName, lastName, email }
  items = [], // array of { label, icon, path?, onClick? }
  navigate,
  setMobileOpen,
  onClose,
}) => {
  // const handleItemClick = item => {
  //   if (item.onClick) item.onClick();
  //   else if (item.path && navigate) navigate(item.path);
  //   if (setMobileOpen) setMobileOpen(false);
  //   onClose();
  // };

  const handleItemClick = (item) => {
    setTimeout(() => {
      if (item.onClick) item.onClick();
      if (item.path && navigate) navigate(item.path);
      if (setMobileOpen) setMobileOpen(false);
      if (onClose) onClose();
    }, 100); // Delay everything including onClose
  };

  return (
    <List>
      {userInfo && (
        <>
          <ListItem>
            <ListItemIcon>
              <AccountCircleOutlinedIcon />
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  style={{ textTransform: "capitalize" }}
                >
                  {userInfo.firstName} {userInfo.lastName}
                </Typography>
              }
              secondary={
                <Typography variant="body2">{userInfo.email}</Typography>
              }
            />
          </ListItem>
          <Divider />
        </>
      )}

      {items.map(({ label, icon, path, onClick }) => (
        <ListItem
          key={label}
          button
          onClick={() => handleItemClick({ label, icon, path, onClick })}
          sx={{ cursor: "pointer" }}
        >
          <ListItemIcon>{icon}</ListItemIcon>
          <ListItemText primary={label} />
        </ListItem>
      ))}
    </List>
  );
};

// SubMenu component using the HOC
const SubMenu = withPopover(SubMenuContent);

// PropTypes for SubMenuContent
SubMenuContent.propTypes = {
  userInfo: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    email: PropTypes.string,
  }),
  items: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      icon: PropTypes.node,
      path: PropTypes.string,
      onClick: PropTypes.func,
    })
  ),
  navigate: PropTypes.func,
  setMobileOpen: PropTypes.func,
  onClose: PropTypes.func.isRequired,
};

// Default props for SubMenuContent
SubMenuContent.defaultProps = {
  items: [],
  userInfo: null,
  navigate: null,
  setMobileOpen: null,
};

// Export both the HOC and the original component
export { withPopover };
export default SubMenu;
